import { useState, useEffect, type ReactNode } from "react";
import { z } from "zod";
import {
  cloudProviderSchemas,
  ToolFilterHookPoints,
  type CloudProviderSchemaTypes,
} from "@pineseed/spec/cloud-provider-schemas";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import type {
  CloudProviderTool,
  CloudProviderHook,
  CloudProviderHitlHandler,
  CloudProviderTenantTool,
  CloudProviderTenantHook,
  CloudProviderTenantHitlHandler,
  CreateCloudProviderHookRequest,
  UpdateCloudProviderHookRequest,
} from "../hook/useCloudProviderAdmin";

// ── Sub-tab types ───────────────────────────────────────────────────────────

type View = 'tools' | 'hooks' | 'hitl' | 'tenant-tools' | 'tenant-hooks' | 'tenant-hitl';

const LAST_VIEW_KEY = 'admin-last-cloud-provider-view';

// ── Zod schemas for forms ───────────────────────────────────────────────────

const CreateToolSchema = z.object({
  handlerName: z.string().min(1).max(255),
  displayName: z.string().min(1).max(255),
  agentName: z.string().min(1).max(255),
  description: z.string().min(1),
  inputSchema: z.string().min(1),
  version: z.string().min(1).max(64),
  timeoutSeconds: z.number().int().positive(),
  preferredMode: z.enum(["sync", "async"]),
  tags: z.string().nullable().optional(),
  category: z.string().max(255).nullable().optional(),
});

const UpdateToolSchema = z.object({
  displayName: z.string().min(1).max(255).optional(),
  agentName: z.string().min(1).max(255).optional(),
  description: z.string().min(1).optional(),
  inputSchema: z.string().min(1).optional(),
  version: z.string().min(1).max(64).optional(),
  timeoutSeconds: z.number().int().positive().optional(),
  preferredMode: z.enum(["sync", "async"]).optional(),
  tags: z.string().nullable().optional(),
  category: z.string().max(255).nullable().optional(),
});

// Derived from the canonical spec so the form shape cannot drift from the API.
const CreateHookSchema = cloudProviderSchemas.CreateCloudProviderHookRequest;

const UpdateHookSchema = cloudProviderSchemas.UpdateCloudProviderHookRequest;

const CreateHitlHandlerSchema =
  cloudProviderSchemas.CreateCloudProviderHitlHandlerRequest;

const UpdateHitlHandlerSchema =
  cloudProviderSchemas.UpdateCloudProviderHitlHandlerRequest;

// ── Column definitions ──────────────────────────────────────────────────────

function StatusBadge({ on }: { on: boolean }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
      on ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
    }`}>
      {on ? 'Yes' : 'No'}
    </span>
  );
}

const toolColumns: Column<Record<string, unknown>>[] = [
  {
    key: "handlerName",
    label: "Name",
    sortable: true,
    render: (item) => (
      <div>
        <div className="font-medium text-gray-900 dark:text-gray-100">{item.displayName as string}</div>
        <div className="text-xs text-gray-500 dark:text-gray-500">{item.handlerName as string} → {item.agentName as string}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate max-w-xs">{item.description as string}</div>
      </div>
    ),
  },
  { key: "version", label: "Version", sortable: true },
  { key: "preferredMode", label: "Mode", sortable: true },
  { key: "timeoutSeconds", label: "Timeout (s)", sortable: true },
  {
    key: "createdAt",
    label: "Created",
    sortable: true,
    render: (item) => <span className="text-xs text-gray-500">{new Date(item.createdAt as string).toLocaleDateString()}</span>,
  },
];

const hookColumns: Column<Record<string, unknown>>[] = [
  {
    key: "name",
    label: "Name",
    sortable: true,
    render: (item) => (
      <div>
        <div className="font-medium text-gray-900 dark:text-gray-100">{item.name as string}</div>
        <ul className="text-xs font-mono text-gray-600 dark:text-gray-400 mt-1">
          {(item.points as CloudProviderHook["points"]).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>
    ),
  },
  { key: "priority", label: "Priority", sortable: true },
  { key: "deliveryMode", label: "Delivery", sortable: true },
  {
    key: "enabled",
    label: "Enabled",
    sortable: true,
    render: (item) => <StatusBadge on={item.enabled === true} />,
  },
  {
    key: "hitlConfig",
    label: "Requires Approval",
    sortable: false,
    render: (item) => <StatusBadge on={item.hitlConfig !== null} />,
  },
  {
    key: "createdAt",
    label: "Created",
    sortable: true,
    render: (item) => <span className="text-xs text-gray-500">{new Date(item.createdAt as string).toLocaleDateString()}</span>,
  },
];

const hitlColumns: Column<Record<string, unknown>>[] = [
  {
    key: "name",
    label: "Name",
    sortable: true,
    render: (item) => <div className="font-medium text-gray-900 dark:text-gray-100">{item.name as string}</div>,
  },
  { key: "handlerType", label: "Type", sortable: true },
  { key: "timeoutSeconds", label: "Timeout (s)", sortable: true },
  {
    key: "createdAt",
    label: "Created",
    sortable: true,
    render: (item) => <span className="text-xs text-gray-500">{new Date(item.createdAt as string).toLocaleDateString()}</span>,
  },
];

// ── Hook form ───────────────────────────────────────────────────────────────
// EntityForm cannot render arrays or a nullable nested object, so hook
// definitions get a dedicated form validated by the same spec schemas.

type HitlConfig = CloudProviderSchemaTypes["HitlConfig"];
type ToolNameFilter = CloudProviderSchemaTypes["ToolNameFilter"];
type HookFormState =
  Partial<Omit<CreateCloudProviderHookRequest, "points" | "toolFilters" | "hitlConfig">> &
  Pick<CreateCloudProviderHookRequest, "points" | "toolFilters"> & {
    hitlConfig?: Partial<HitlConfig> | null;
  };

const HOOK_POINTS = cloudProviderSchemas.HookPointName.options;
const HOOK_PRIORITY = cloudProviderSchemas.CloudProviderHook.shape.priority;
const HITL_SHAPE = cloudProviderSchemas.HitlConfig.shape;

const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100";

function Field({ label, error, children }: { label: string; error: string | undefined; children: ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
        <span className="text-red-500 ml-1">*</span>
      </label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

function EnumSelect<V extends string>({ value, options, onChange }: {
  value: V | undefined;
  options: readonly V[];
  onChange: (value: V | undefined) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(options.find((opt) => opt === e.target.value))}
      required
      className={inputClass}
    >
      <option value="">Select...</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

const BOOLEAN_OPTIONS = ['Yes', 'No'] as const;

function BooleanSelect({ value, onChange }: {
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
}) {
  return (
    <EnumSelect
      value={value === undefined ? undefined : value ? 'Yes' : 'No'}
      options={BOOLEAN_OPTIONS}
      onChange={(opt) => onChange(opt === undefined ? undefined : opt === 'Yes')}
    />
  );
}

function NumberInput({ value, min, max, onChange }: {
  value: number | undefined;
  min?: number | undefined;
  max?: number | undefined;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      required
      min={min}
      max={max}
      className={inputClass}
    />
  );
}

interface HookFormProps<T> {
  schema: z.ZodType<T> & { shape: object };
  initialData: HookFormState;
  onSubmit: (data: T) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}

function HookForm<T>({ schema, initialData, onSubmit, onCancel, submitLabel }: HookFormProps<T>) {
  const [formData, setFormData] = useState<HookFormState>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof HookFormState>(key: K, value: HookFormState[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const setHitl = <K extends keyof HitlConfig>(key: K, value: HitlConfig[K] | undefined) => {
    setFormData((prev) => ({ ...prev, hitlConfig: { ...prev.hitlConfig, [key]: value } }));
  };

  const setToolFilter = (index: number, value: ToolNameFilter) => {
    set("toolFilters", formData.toolFilters.map((t, i) => (i === index ? value : t)));
  };

  const togglePoint = (point: string) => {
    set("points", formData.points.includes(point)
      ? formData.points.filter((p) => p !== point)
      : [...formData.points, point]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = schema.safeParse(formData);
    if (!result.success) {
      setErrors(Object.fromEntries(result.error.issues.map((issue) => [issue.path.join('.'), issue.message])));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(result.data);
    } catch (error) {
      setErrors({ _global: error instanceof Error ? error.message : String(error) });
    } finally {
      setSubmitting(false);
    }
  };

  const hitlConfig = formData.hitlConfig;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white dark:bg-gray-900 rounded-lg shadow">
      {errors._global && (
        <div className="p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
          {errors._global}
        </div>
      )}

      {"name" in schema.shape && (
        <Field label="Name" error={errors.name}>
          <input
            type="text"
            value={formData.name ?? ''}
            onChange={(e) => set("name", e.target.value === '' ? undefined : e.target.value)}
            required
            className={inputClass}
          />
        </Field>
      )}

      <Field label="Points" error={errors.points}>
        <div className="border border-gray-300 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800 space-y-1">
          {HOOK_POINTS.map((point) => (
            <label key={point} className="flex items-center gap-2 cursor-pointer text-sm font-mono text-gray-900 dark:text-gray-100">
              <input
                type="checkbox"
                checked={formData.points.includes(point)}
                onChange={() => togglePoint(point)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              {point}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Priority" error={errors.priority}>
        <NumberInput
          value={formData.priority}
          min={HOOK_PRIORITY.minValue ?? undefined}
          max={HOOK_PRIORITY.maxValue ?? undefined}
          onChange={(v) => set("priority", v)}
        />
      </Field>

      <Field label="Timeout Policy" error={errors.timeoutPolicy}>
        <EnumSelect
          value={formData.timeoutPolicy}
          options={cloudProviderSchemas.TimeoutPolicy.options}
          onChange={(v) => set("timeoutPolicy", v)}
        />
      </Field>

      <Field label="Delivery Mode" error={errors.deliveryMode}>
        <EnumSelect
          value={formData.deliveryMode}
          options={cloudProviderSchemas.DeliveryMode.options}
          onChange={(v) => set("deliveryMode", v)}
        />
      </Field>

      <Field label="Timeout Seconds" error={errors.timeoutSeconds}>
        <NumberInput value={formData.timeoutSeconds} onChange={(v) => set("timeoutSeconds", v)} />
      </Field>

      <Field label="Enabled" error={errors.enabled}>
        <BooleanSelect value={formData.enabled} onChange={(v) => set("enabled", v)} />
      </Field>

      <Field label="Tool Filters" error={errors.toolFilters}>
        <div className="space-y-2">
          {formData.toolFilters.map((tool, index) => (
            <div key={index}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tool.name}
                  onChange={(e) => setToolFilter(index, { ...tool, name: e.target.value })}
                  required
                  placeholder="Tool name"
                  className={inputClass}
                />
                <input
                  type="text"
                  value={tool.location ?? ''}
                  onChange={(e) => setToolFilter(index, { ...tool, location: e.target.value === '' ? null : e.target.value })}
                  placeholder="Location (empty = any)"
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={() => set("toolFilters", formData.toolFilters.filter((_, i) => i !== index))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Remove
                </button>
              </div>
              {(['', '.name', '.location'] as const).map((suffix) => {
                const error = errors[`toolFilters.${index}${suffix}`];
                return error && (
                  <p key={suffix} className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
                );
              })}
            </div>
          ))}
          <button
            type="button"
            onClick={() => set("toolFilters", [...formData.toolFilters, { name: '', location: null }])}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Add Tool
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No tools = the hook applies to all tools. Tool filters apply only at {ToolFilterHookPoints.join(' / ')}.
          </p>
        </div>
      </Field>

      <Field label="Requires Approval" error={errors.hitlConfig}>
        <BooleanSelect
          value={hitlConfig === undefined ? undefined : hitlConfig !== null}
          onChange={(v) => set("hitlConfig", v === undefined ? undefined : v ? {} : null)}
        />
      </Field>

      {hitlConfig && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
          <Field label="Approval Enabled" error={errors["hitlConfig.enabled"]}>
            <BooleanSelect value={hitlConfig.enabled} onChange={(v) => setHitl("enabled", v)} />
          </Field>
          <Field label="On Timeout Policy" error={errors["hitlConfig.onTimeoutPolicy"]}>
            <EnumSelect
              value={hitlConfig.onTimeoutPolicy}
              options={HITL_SHAPE.onTimeoutPolicy.options}
              onChange={(v) => setHitl("onTimeoutPolicy", v)}
            />
          </Field>
          <Field label="Gating Scope" error={errors["hitlConfig.gatingScope"]}>
            <EnumSelect
              value={hitlConfig.gatingScope}
              options={HITL_SHAPE.gatingScope.options}
              onChange={(v) => setHitl("gatingScope", v)}
            />
          </Field>
          <Field label="Approval Timeout Seconds" error={errors["hitlConfig.timeoutSeconds"]}>
            <NumberInput value={hitlConfig.timeoutSeconds} onChange={(v) => setHitl("timeoutSeconds", v)} />
          </Field>
          <Field label="Steering Scope" error={errors["hitlConfig.steeringScope"]}>
            <EnumSelect
              value={hitlConfig.steeringScope}
              options={HITL_SHAPE.steeringScope.options}
              onChange={(v) => setHitl("steeringScope", v)}
            />
          </Field>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Submitting...' : submitLabel}
        </button>
      </div>
    </form>
  );
}

const EMPTY_HOOK_FORM: HookFormState = { points: [], toolFilters: [] };

// ── View labels ─────────────────────────────────────────────────────────────

const WORKSPACE_VIEWS: View[] = ['tools', 'hooks', 'hitl'];
const TENANT_VIEWS: View[] = ['tenant-tools', 'tenant-hooks', 'tenant-hitl'];

const viewLabels: Record<View, string> = {
  tools: 'Workspace Tools',
  hooks: 'Workspace Hooks',
  hitl: 'Workspace HITL Handlers',
  'tenant-tools': 'Tenant Tools',
  'tenant-hooks': 'Tenant Hooks',
  'tenant-hitl': 'Tenant HITL Handlers',
};

// ── Main panel ──────────────────────────────────────────────────────────────

export function CloudProviderPanel() {
  const { cloudProvider, account } = useAdmin();

  const [view, setView] = useState<View>(() => {
    const saved = localStorage.getItem(LAST_VIEW_KEY) as View | null;
    return saved || 'tools';
  });

  // Persist view
  useEffect(() => {
    localStorage.setItem(LAST_VIEW_KEY, view);
  }, [view]);

  // ── Form state — Workspace ─────────────────────────────────────────────

  const [showCreateTool, setShowCreateTool] = useState(false);
  const [editingTool, setEditingTool] = useState<CloudProviderTool | null>(null);

  const [showCreateHook, setShowCreateHook] = useState(false);
  const [editingHook, setEditingHook] = useState<CloudProviderHook | null>(null);

  const [showCreateHitl, setShowCreateHitl] = useState(false);
  const [editingHitl, setEditingHitl] = useState<CloudProviderHitlHandler | null>(null);

  // ── Form state — Tenant ────────────────────────────────────────────────

  const [showCreateTenantTool, setShowCreateTenantTool] = useState(false);
  const [editingTenantTool, setEditingTenantTool] = useState<CloudProviderTenantTool | null>(null);

  const [showCreateTenantHook, setShowCreateTenantHook] = useState(false);
  const [editingTenantHook, setEditingTenantHook] = useState<CloudProviderTenantHook | null>(null);

  const [showCreateTenantHitl, setShowCreateTenantHitl] = useState(false);
  const [editingTenantHitl, setEditingTenantHitl] = useState<CloudProviderTenantHitlHandler | null>(null);

  // ── Handlers — Workspace Tools ─────────────────────────────────────────

  const handleCreateTool = async (data: Record<string, unknown>) => {
    await cloudProvider.upsertTool(data);
    setShowCreateTool(false);
  };

  const handleEditTool = async (data: Record<string, unknown>) => {
    if (!editingTool) return;
    await cloudProvider.updateTool(editingTool.handlerName, data);
    setEditingTool(null);
  };

  const handleDeleteTool = async (item: CloudProviderTool) => {
    if (!confirm(`Delete tool "${item.displayName}"?`)) return;
    await cloudProvider.deleteTool(item.handlerName);
  };

  // ── Handlers — Workspace Hooks ─────────────────────────────────────────

  const handleCreateHook = async (data: CreateCloudProviderHookRequest) => {
    await cloudProvider.upsertHook(data);
    setShowCreateHook(false);
  };

  const handleEditHook = async (data: UpdateCloudProviderHookRequest) => {
    if (!editingHook) return;
    await cloudProvider.updateHook(editingHook.name, data);
    setEditingHook(null);
  };

  const handleDeleteHook = async (item: CloudProviderHook) => {
    if (!confirm(`Delete hook "${item.name}"?`)) return;
    await cloudProvider.deleteHook(item.name);
  };

  // ── Handlers — Workspace HITL Handlers ────────────────────────────────

  const handleCreateHitl = async (data: Record<string, unknown>) => {
    await cloudProvider.upsertHitlHandler(data);
    setShowCreateHitl(false);
  };

  const handleEditHitl = async (data: Record<string, unknown>) => {
    if (!editingHitl) return;
    await cloudProvider.updateHitlHandler(editingHitl.name, data);
    setEditingHitl(null);
  };

  const handleDeleteHitl = async (item: CloudProviderHitlHandler) => {
    if (!confirm(`Delete HITL handler "${item.name}"?`)) return;
    await cloudProvider.deleteHitlHandler(item.name);
  };

  // ── Handlers — Tenant Tools ────────────────────────────────────────────

  const handleCreateTenantTool = async (data: Record<string, unknown>) => {
    await cloudProvider.upsertTenantTool(data);
    setShowCreateTenantTool(false);
  };

  const handleEditTenantTool = async (data: Record<string, unknown>) => {
    if (!editingTenantTool) return;
    await cloudProvider.updateTenantTool(editingTenantTool.handlerName, data);
    setEditingTenantTool(null);
  };

  const handleDeleteTenantTool = async (item: CloudProviderTenantTool) => {
    if (!confirm(`Delete tenant tool "${item.displayName}"?`)) return;
    await cloudProvider.deleteTenantTool(item.handlerName);
  };

  // ── Handlers — Tenant Hooks ────────────────────────────────────────────

  const handleCreateTenantHook = async (data: CreateCloudProviderHookRequest) => {
    await cloudProvider.upsertTenantHook(data);
    setShowCreateTenantHook(false);
  };

  const handleEditTenantHook = async (data: UpdateCloudProviderHookRequest) => {
    if (!editingTenantHook) return;
    await cloudProvider.updateTenantHook(editingTenantHook.name, data);
    setEditingTenantHook(null);
  };

  const handleDeleteTenantHook = async (item: CloudProviderTenantHook) => {
    if (!confirm(`Delete tenant hook "${item.name}"?`)) return;
    await cloudProvider.deleteTenantHook(item.name);
  };

  // ── Handlers — Tenant HITL Handlers ───────────────────────────────────

  const handleCreateTenantHitl = async (data: Record<string, unknown>) => {
    await cloudProvider.upsertTenantHitlHandler(data);
    setShowCreateTenantHitl(false);
  };

  const handleEditTenantHitl = async (data: Record<string, unknown>) => {
    if (!editingTenantHitl) return;
    await cloudProvider.updateTenantHitlHandler(editingTenantHitl.name, data);
    setEditingTenantHitl(null);
  };

  const handleDeleteTenantHitl = async (item: CloudProviderTenantHitlHandler) => {
    if (!confirm(`Delete tenant HITL handler "${item.name}"?`)) return;
    await cloudProvider.deleteTenantHitlHandler(item.name);
  };

  // ── Create button handler per view ────────────────────────────────────

  const createHandlers: Record<View, () => void> = {
    tools: () => setShowCreateTool(true),
    hooks: () => setShowCreateHook(true),
    hitl: () => setShowCreateHitl(true),
    'tenant-tools': () => setShowCreateTenantTool(true),
    'tenant-hooks': () => setShowCreateTenantHook(true),
    'tenant-hitl': () => setShowCreateTenantHitl(true),
  };

  // ── View count resolver ───────────────────────────────────────────────

  const viewCounts: Record<View, number> = {
    tools: cloudProvider.tools.length,
    hooks: cloudProvider.hooks.length,
    hitl: cloudProvider.hitlHandlers.length,
    'tenant-tools': cloudProvider.tenantTools.length,
    'tenant-hooks': cloudProvider.tenantHooks.length,
    'tenant-hitl': cloudProvider.tenantHitlHandlers.length,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cloud Provider</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage tools, hooks, and HITL handlers for the workspace and tenant
          </p>
        </div>
        <button
          onClick={createHandlers[view]}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create {viewLabels[view].replace(/s$/, '')}
        </button>
      </div>

      {/* Account selector (for tenant-scoped operations) */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Account:</label>
        <select
          value={cloudProvider.selectedAccountId}
          onChange={(e) => cloudProvider.setSelectedAccountId(e.target.value)}
          className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Select account...</option>
          {account.accounts.entities.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        {!cloudProvider.selectedAccountId && (
          <span className="text-xs text-amber-600 dark:text-amber-400">Required for mutations and tenant data</span>
        )}
      </div>

      {/* Error banner */}
      {cloudProvider.error && (
        <div className="p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
          {cloudProvider.error}
        </div>
      )}

      {/* View Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {WORKSPACE_VIEWS.map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 border-b-2 transition-colors ${view === v
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
            >
              {viewLabels[v]}
              <span className="ml-2 text-xs">
                ({viewCounts[v]})
              </span>
            </button>
          ))}

          {/* Separator */}
          <div className="self-center h-6 w-px bg-gray-300 dark:bg-gray-600 mx-2" />

          {TENANT_VIEWS.map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 border-b-2 transition-colors ${view === v
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
            >
              {viewLabels[v]}
              <span className="ml-2 text-xs">
                ({viewCounts[v]})
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Workspace Tools Table */}
      {view === 'tools' && (
        <EntityTable
          data={cloudProvider.tools as unknown as Record<string, unknown>[]}
          columns={toolColumns}
          onEdit={(item) => setEditingTool(item as unknown as CloudProviderTool)}
          onDelete={(item) => handleDeleteTool(item as unknown as CloudProviderTool)}
          loading={cloudProvider.loading}
          emptyMessage="No tools found. Create your first tool to get started."
        />
      )}

      {/* Workspace Hooks Table */}
      {view === 'hooks' && (
        <EntityTable
          data={cloudProvider.hooks as unknown as Record<string, unknown>[]}
          columns={hookColumns}
          onEdit={(item) => setEditingHook(item as unknown as CloudProviderHook)}
          onDelete={(item) => handleDeleteHook(item as unknown as CloudProviderHook)}
          loading={cloudProvider.loading}
          emptyMessage="No hooks found. Create your first hook to get started."
        />
      )}

      {/* Workspace HITL Handlers Table */}
      {view === 'hitl' && (
        <EntityTable
          data={cloudProvider.hitlHandlers as unknown as Record<string, unknown>[]}
          columns={hitlColumns}
          onEdit={(item) => setEditingHitl(item as unknown as CloudProviderHitlHandler)}
          onDelete={(item) => handleDeleteHitl(item as unknown as CloudProviderHitlHandler)}
          loading={cloudProvider.loading}
          emptyMessage="No HITL handlers found. Create your first handler to get started."
        />
      )}

      {/* Tenant Tools Table */}
      {view === 'tenant-tools' && (
        <EntityTable
          data={cloudProvider.tenantTools as unknown as Record<string, unknown>[]}
          columns={toolColumns}
          onEdit={(item) => setEditingTenantTool(item as unknown as CloudProviderTenantTool)}
          onDelete={(item) => handleDeleteTenantTool(item as unknown as CloudProviderTenantTool)}
          loading={cloudProvider.loading}
          emptyMessage="No tenant tools found. Create your first tenant tool to get started."
        />
      )}

      {/* Tenant Hooks Table */}
      {view === 'tenant-hooks' && (
        <EntityTable
          data={cloudProvider.tenantHooks as unknown as Record<string, unknown>[]}
          columns={hookColumns}
          onEdit={(item) => setEditingTenantHook(item as unknown as CloudProviderTenantHook)}
          onDelete={(item) => handleDeleteTenantHook(item as unknown as CloudProviderTenantHook)}
          loading={cloudProvider.loading}
          emptyMessage="No tenant hooks found. Create your first tenant hook to get started."
        />
      )}

      {/* Tenant HITL Handlers Table */}
      {view === 'tenant-hitl' && (
        <EntityTable
          data={cloudProvider.tenantHitlHandlers as unknown as Record<string, unknown>[]}
          columns={hitlColumns}
          onEdit={(item) => setEditingTenantHitl(item as unknown as CloudProviderTenantHitlHandler)}
          onDelete={(item) => handleDeleteTenantHitl(item as unknown as CloudProviderTenantHitlHandler)}
          loading={cloudProvider.loading}
          emptyMessage="No tenant HITL handlers found. Create your first tenant handler to get started."
        />
      )}

      {/* Create Tool Modal */}
      <Modal isOpen={showCreateTool} onClose={() => setShowCreateTool(false)} title="Create Tool">
        <EntityForm
          schema={CreateToolSchema}
          onSubmit={handleCreateTool}
          onCancel={() => setShowCreateTool(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Tool Modal */}
      <Modal isOpen={!!editingTool} onClose={() => setEditingTool(null)} title="Edit Tool">
        {editingTool && (
          <>
            <div className="mb-4 px-1">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Handler Name</span>
              <div className="text-sm font-mono text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 mt-0.5">{editingTool.handlerName}</div>
            </div>
            <EntityForm
              schema={UpdateToolSchema}
              initialData={editingTool as unknown as Record<string, unknown>}
              onSubmit={handleEditTool}
              onCancel={() => setEditingTool(null)}
              submitLabel="Update"
            />
          </>
        )}
      </Modal>

      {/* Create Hook Modal */}
      <Modal isOpen={showCreateHook} onClose={() => setShowCreateHook(false)} title="Create Hook">
        <HookForm
          schema={CreateHookSchema}
          initialData={EMPTY_HOOK_FORM}
          onSubmit={handleCreateHook}
          onCancel={() => setShowCreateHook(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Hook Modal */}
      <Modal isOpen={!!editingHook} onClose={() => setEditingHook(null)} title="Edit Hook">
        {editingHook && (
          <HookForm
            schema={UpdateHookSchema}
            initialData={editingHook}
            onSubmit={handleEditHook}
            onCancel={() => setEditingHook(null)}
            submitLabel="Update"
          />
        )}
      </Modal>

      {/* Create HITL Handler Modal */}
      <Modal isOpen={showCreateHitl} onClose={() => setShowCreateHitl(false)} title="Create HITL Handler">
        <EntityForm
          schema={CreateHitlHandlerSchema}
          onSubmit={handleCreateHitl}
          onCancel={() => setShowCreateHitl(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit HITL Handler Modal */}
      <Modal isOpen={!!editingHitl} onClose={() => setEditingHitl(null)} title="Edit HITL Handler">
        {editingHitl && (
          <EntityForm
            schema={UpdateHitlHandlerSchema}
            initialData={editingHitl as unknown as Record<string, unknown>}
            onSubmit={handleEditHitl}
            onCancel={() => setEditingHitl(null)}
            submitLabel="Update"
          />
        )}
      </Modal>

      {/* Create Tenant Tool Modal */}
      <Modal isOpen={showCreateTenantTool} onClose={() => setShowCreateTenantTool(false)} title="Create Tenant Tool">
        <EntityForm
          schema={CreateToolSchema}
          onSubmit={handleCreateTenantTool}
          onCancel={() => setShowCreateTenantTool(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Tenant Tool Modal */}
      <Modal isOpen={!!editingTenantTool} onClose={() => setEditingTenantTool(null)} title="Edit Tenant Tool">
        {editingTenantTool && (
          <>
            <div className="mb-4 px-1">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Handler Name</span>
              <div className="text-sm font-mono text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 mt-0.5">{editingTenantTool.handlerName}</div>
            </div>
            <EntityForm
              schema={UpdateToolSchema}
              initialData={editingTenantTool as unknown as Record<string, unknown>}
              onSubmit={handleEditTenantTool}
              onCancel={() => setEditingTenantTool(null)}
              submitLabel="Update"
            />
          </>
        )}
      </Modal>

      {/* Create Tenant Hook Modal */}
      <Modal isOpen={showCreateTenantHook} onClose={() => setShowCreateTenantHook(false)} title="Create Tenant Hook">
        <HookForm
          schema={CreateHookSchema}
          initialData={EMPTY_HOOK_FORM}
          onSubmit={handleCreateTenantHook}
          onCancel={() => setShowCreateTenantHook(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Tenant Hook Modal */}
      <Modal isOpen={!!editingTenantHook} onClose={() => setEditingTenantHook(null)} title="Edit Tenant Hook">
        {editingTenantHook && (
          <HookForm
            schema={UpdateHookSchema}
            initialData={editingTenantHook}
            onSubmit={handleEditTenantHook}
            onCancel={() => setEditingTenantHook(null)}
            submitLabel="Update"
          />
        )}
      </Modal>

      {/* Create Tenant HITL Handler Modal */}
      <Modal isOpen={showCreateTenantHitl} onClose={() => setShowCreateTenantHitl(false)} title="Create Tenant HITL Handler">
        <EntityForm
          schema={CreateHitlHandlerSchema}
          onSubmit={handleCreateTenantHitl}
          onCancel={() => setShowCreateTenantHitl(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Tenant HITL Handler Modal */}
      <Modal isOpen={!!editingTenantHitl} onClose={() => setEditingTenantHitl(null)} title="Edit Tenant HITL Handler">
        {editingTenantHitl && (
          <EntityForm
            schema={UpdateHitlHandlerSchema}
            initialData={editingTenantHitl as unknown as Record<string, unknown>}
            onSubmit={handleEditTenantHitl}
            onCancel={() => setEditingTenantHitl(null)}
            submitLabel="Update"
          />
        )}
      </Modal>
    </div>
  );
}
